import type {
  WeedAbstentionReason,
  WeedIdentificationResult,
  WeedImageRole,
} from '@/lib/types/weed-identification'

/**
 * One overview plus at most two supporting views. This ceiling is also enforced
 * server-side (`maxImagesForMode` in app/api/ai/analyze-grass/route.ts) and
 * matches the two supporting slots Identify and Diagnose expose through
 * hooks/use-homepage-analysis.ts.
 */
export const WEED_MAX_ANALYSIS_PHOTOS = 3
export const WEED_PRIMARY_ROLE: WeedImageRole = 'overview'
export const WEED_SUPPORTING_ROLES: readonly WeedImageRole[] = ['leaf_stem', 'diagnostic_feature']
export const WEED_ROLE_ORDER: readonly WeedImageRole[] = [WEED_PRIMARY_ROLE, ...WEED_SUPPORTING_ROLES]

/**
 * Abstention reasons that another photo can actually move.
 *
 * `unsupported_weed` is a catalog gap, not an evidence gap: the model read the
 * plant confidently and it simply is not in WEED_REGISTRY, so accumulating
 * views cannot change the outcome. `analysis_failed` never reached the model,
 * and `not_a_weed` is a subject judgement rather than a missing view. Those all
 * fall back to a plain new-photo retry.
 */
export const WEED_ACCUMULATING_ABSTENTION_REASONS: readonly WeedAbstentionReason[] = [
  'insufficient_visual_evidence',
  'poor_image',
  'low_candidate_margin',
  'contradictory_evidence',
]

const ACCUMULATING_REASON_SET = new Set<string>(WEED_ACCUMULATING_ABSTENTION_REASONS)

/** Structural view of a client photo slot, so this module stays UI-free. */
export interface WeedPhotoLike {
  role: WeedImageRole
  remoteUrl?: string
}

type AccumulationDecisionInput = Pick<WeedIdentificationResult, 'outcome' | 'abstentionReason'>

/** True when the result should offer "Add another photo" instead of a retry. */
export function canAccumulateWeedPhotos(
  result: AccumulationDecisionInput | null | undefined,
): boolean {
  if (!result) return false
  if (result.outcome === 'supported' || result.outcome === 'unsupported') return false
  return typeof result.abstentionReason === 'string' && ACCUMULATING_REASON_SET.has(result.abstentionReason)
}

/** Primary first, supporting views in their canonical order. */
export function sortWeedPhotos<T extends WeedPhotoLike>(photos: readonly T[]): T[] {
  return [...photos].sort(
    (a, b) => WEED_ROLE_ORDER.indexOf(a.role) - WEED_ROLE_ORDER.indexOf(b.role),
  )
}

/** Free supporting slots left, mirroring `Math.max(0, 2 - supportingImages.length)`. */
export function weedSupportingSlotsRemaining(photos: readonly WeedPhotoLike[]): number {
  const filled = new Set(
    photos.filter((photo) => photo.role !== WEED_PRIMARY_ROLE).map((photo) => photo.role),
  )
  return Math.max(0, WEED_SUPPORTING_ROLES.length - filled.size)
}

/** The supporting slot to ask for next, or null once both are filled. */
export function nextWeedSupportingRole(photos: readonly WeedPhotoLike[]): WeedImageRole | null {
  return WEED_SUPPORTING_ROLES.find((role) => !photos.some((photo) => photo.role === role)) ?? null
}

/** URLs the source scan already sent, used as the refinement baseline. */
export function weedRefinementBaselineUrls(
  result: Pick<WeedIdentificationResult, 'image' | 'imageUrls'> | null | undefined,
): string[] {
  const candidates = [result?.image, ...(result?.imageUrls ?? [])]
  return Array.from(new Set(candidates.filter((url): url is string => typeof url === 'string' && url.length > 0)))
}

/**
 * A refinement must contribute at least one photo the source scan never saw.
 * `resolveRescanExemption` fails closed when every incoming URL is already
 * attached to the source row, so replaying the same set spends a Diagnosis
 * credit instead of running free. An empty baseline means the source set is
 * unknown (a session resumed from storage written before the baseline was
 * persisted), so require any supporting view rather than blocking the user.
 */
export function weedRefinementHasNewEvidence(input: {
  photos: readonly WeedPhotoLike[]
  baselineUrls: readonly string[]
}): boolean {
  const baseline = new Set(input.baselineUrls.filter((url) => Boolean(url)))
  if (baseline.size === 0) {
    return input.photos.some((photo) => photo.role !== WEED_PRIMARY_ROLE)
  }
  return input.photos.some((photo) => !photo.remoteUrl || !baseline.has(photo.remoteUrl))
}

/**
 * The view to ask for next. The model names the separating feature it could not
 * see, which is far more actionable than a generic "add another photo".
 */
export function weedRefinementGuidance(
  result: Pick<WeedIdentificationResult, 'recommendedNextPhoto' | 'missingEvidence'> | null | undefined,
): string {
  const recommended = result?.recommendedNextPhoto?.replace(/\s+/g, ' ').trim()
  if (recommended) return recommended
  const missing = (result?.missingEvidence ?? [])
    .map((item) => (typeof item === 'string' ? item.replace(/\s+/g, ' ').trim() : ''))
    .find((item) => item.length > 0)
  if (missing) return `Add a close-up that shows ${missing}.`
  return 'Add a close-up of the leaf, stem, and any flower or seed head.'
}
